import { IconChevronLeft16, IconChevronRight16, UIHeading5 } from '@cian/ui-kit';
import { max, min } from 'date-fns';
import { splitEvery } from 'ramda';
import * as React from 'react';

import { formatDate } from 'shared/containers/BookingCalendarInputContainer/helpers';
import { useCountSelectedDates } from 'shared/containers/BookingCalendarInputContainer/hooks';

import { calendarReducer } from './reducer';
import { ECalendarActionTypes, ICalendarContainerProps } from './types';
import { prepareInitialState } from './utils';
import { ArrowButton } from '../../components/ArrowButton';
import { Calendar } from '../../components/CalendarSection';
import { Cell } from '../../components/Cell';
import { Month } from '../../components/Month';
import { Week } from '../../components/Week';
import { DAYS_OF_WEEK, MONTHS, MONTHS_TO_DISPLAY } from '../../constants';
import { ICalendarDay, TCalendarDay } from '../../types';

export const CalendarContainer = ({ onChange, isDayDisabled, ...props }: ICalendarContainerProps) => {
  const [state, dispatch] = React.useReducer(calendarReducer, prepareInitialState({ ...props, isDayDisabled }));

  const { selectedDatesCount, countSelectedDates } = useCountSelectedDates();

  const visibleMonths = React.useMemo(() => {
    return state.calendar.slice(state.currentPage, state.currentPage + MONTHS_TO_DISPLAY);
  }, [state.calendar, state.currentPage]);

  const handlePrevClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.nativeEvent.stopImmediatePropagation();

      dispatch({ type: ECalendarActionTypes.PrevPage });

      if (props.onPrevClick) {
        props.onPrevClick();
      }
    },
    [props.onPrevClick],
  );

  const handleNextClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.nativeEvent.stopImmediatePropagation();

      dispatch({ type: ECalendarActionTypes.NextPage });

      if (props.onNextClick) {
        props.onNextClick();
      }
    },
    [props.onNextClick],
  );

  const handleCellClick = React.useCallback(
    (day: ICalendarDay) => () => {
      countSelectedDates();

      switch (selectedDatesCount.current) {
        case 1: {
          return onChange(day.date, day.date, selectedDatesCount.current);
        }
        case 2: {
          /*
           * тут не важно, state.from взять или state.to, потому что
           * после выбора первой даты они обе устанавливаются в одинаковое значение
           */
          const stateFromDate = state.from;

          if (!stateFromDate) {
            throw new Error('at CalendarContainer: state.from неожиданно null.');
          }

          const startDate = min([stateFromDate, day.date]);
          const endDate = max([stateFromDate, day.date]);

          return onChange(startDate, endDate, selectedDatesCount.current);
        }
      }
    },
    [state.from, state.to, onChange],
  );

  React.useEffect(() => {
    if (!props.fromValue || !props.toValue) {
      dispatch({ type: ECalendarActionTypes.SetRange, payload: { from: null, to: null } });

      return;
    }
    dispatch({ type: ECalendarActionTypes.SetRange, payload: { from: props.fromValue, to: props.toValue } });
  }, [props.fromValue, props.toValue]);

  const prevButton = state.isPrevPageAvailable && (
    <ArrowButton onClick={handlePrevClick}>
      <IconChevronLeft16 color="primary_100" />
    </ArrowButton>
  );
  const nextButton = state.isNextPageAvailable && (
    <ArrowButton onClick={handleNextClick}>
      <IconChevronRight16 color="primary_100" />
    </ArrowButton>
  );

  const months = visibleMonths.map((month, monthIndex) => {
    const { number, year, days } = month;

    return (
      <Month
        key={monthIndex}
        header={
          <UIHeading5>
            {MONTHS[number]}&nbsp;{year}
          </UIHeading5>
        }
      >
        <Week>
          {DAYS_OF_WEEK.map((text, index) => (
            <Cell key={index} type="day_of_week" content={text} />
          ))}
        </Week>
        {splitEvery(7, days).map((week: TCalendarDay[], index) => (
          <Week key={`week-${index}`}>
            {week.map((day, dayIndex) => {
              if (!day) {
                return <Cell key={dayIndex} type="none" />;
              }

              const date = formatDate(day.date);

              let onClick: VoidFunction | undefined = handleCellClick(day);

              if (day.type === 'disabled') {
                onClick = undefined;
              }

              return <Cell key={dayIndex} type={day.type} content={day.number} onClick={onClick} date={date} />;
            })}
          </Week>
        ))}
      </Month>
    );
  });

  return <Calendar months={months} prevButton={prevButton} nextButton={nextButton} />;
};
